/**
 * Mock API Service
 *
 * Simulates backend API calls for Sprint 1 MVP development.
 * Returns promises with realistic delays to simulate network requests.
 *
 * Sprint 1: No real backend - all data from mock files
 * Sprint 2+: Replace with real API calls
 */

import type { User } from '../types/user';
import type { Organization } from '../types/organization';
import type { Application, ApplicationStatus } from '../types/application';
import type { Supplier } from '../types/supplier';
import type { TrainingCourse, TrainingSession, TrainingEnrollment } from '../types/training';
import type { Payment, Invoice, PaymentStatus } from '../types/payment';
import type { CurrencyInfo, ExchangeRate } from '../data/currencies';
import type { Document } from '../data/documents';
import type { ChecklistTemplate } from '../data/checklistTemplates';
import type { Certificate, CertificateStatus, CertificateVerificationResult, CertificateStatistics } from '../types/certificate';
import type { SupportTicket, TicketMessage, KnowledgeBaseArticle, FAQ, SupportStats, TicketStatus, TicketPriority, TicketType, KnowledgeBaseCategory } from '../types/support';

// Import mock data
import { getUserById, getUserByEmail, getUsersByOrganizationId } from '../data/users';
import { mockOrganizations, getOrganizationById } from '../data/organizations';
import { mockApplications, getApplicationById, getApplicationsByCompany, getApplicationsByStatus } from '../data/applications';
import { mockSuppliers, getSupplierById, getCertifiedSuppliers } from '../data/suppliers';
import { mockTrainingCourses, mockTrainingSessions, mockTrainingEnrollments, getCourseById, getUpcomingSessions } from '../data/training';
import { mockPayments, mockInvoices, getPaymentById, getPaymentsByStatus, getOverdueInvoices } from '../data/payments';
import { mockCurrencies, getCurrencyInfo, getExchangeRate, convertCurrency } from '../data/currencies';
import { getDocumentById, getDocumentsByCompany, getExpiringDocuments } from '../data/documents';
import { mockChecklistTemplates, getChecklistTemplateById, getChecklistTemplatesByType } from '../data/checklistTemplates';
import { mockCertificates, getCertificateById, getCertificateByCertNumber, getCertificatesByCompany, getCertificatesByStatus, getActiveCertificates, getExpiringCertificates, verifyCertificate, getCertificateStatistics } from '../data/certificates';
import { mockTickets, mockTicketMessages, mockKnowledgeBaseArticles, mockFAQs, getTicketById, getTickets, getTicketMessages, getKnowledgeBaseArticles, getArticleBySlug, getFAQs, getSupportStats } from '../data/support';

/**
 * Simulates network delay
 */
function delay(ms: number = 500): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * Mock API response wrapper
 */
interface ApiResponse<T> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

/**
 * Mock API Service
 */
export const mockApi = {
  // ===== Authentication =====

  /**
   * Login user
   */
  async login(email: string, _password: string): Promise<ApiResponse<User>> {
    await delay(800);

    const user = getUserByEmail(email);
    if (!user) {
      return { success: false, error: 'Invalid email or password' };
    }

    // Mock: Accept any password for demo
    return { success: true, data: user };
  },

  /**
   * Get current user
   */
  async getCurrentUser(userId: string): Promise<ApiResponse<User>> {
    await delay(300);

    const user = getUserById(userId);
    if (!user) {
      return { success: false, error: 'User not found' };
    }

    return { success: true, data: user };
  },

  // ===== Organizations =====

  /**
   * Get organization by ID
   */
  async getOrganization(id: string): Promise<ApiResponse<Organization>> {
    await delay(400);

    const org = getOrganizationById(id);
    if (!org) {
      return { success: false, error: 'Organization not found' };
    }

    return { success: true, data: org };
  },

  /**
   * Get all organizations
   */
  async getOrganizations(): Promise<ApiResponse<Organization[]>> {
    await delay(500);
    return { success: true, data: mockOrganizations };
  },

  // ===== Users =====

  /**
   * Get users by organization
   */
  async getOrganizationUsers(orgId: string): Promise<ApiResponse<User[]>> {
    await delay(400);

    const users = getUsersByOrganizationId(orgId);
    return { success: true, data: users };
  },

  // ===== Applications =====

  /**
   * Get application by ID
   */
  async getApplication(id: string): Promise<ApiResponse<Application>> {
    await delay(500);

    const application = getApplicationById(id);
    if (!application) {
      return { success: false, error: 'Application not found' };
    }

    return { success: true, data: application };
  },

  /**
   * Get applications by company
   */
  async getCompanyApplications(companyId: string): Promise<ApiResponse<Application[]>> {
    await delay(600);

    const applications = getApplicationsByCompany(companyId);
    return { success: true, data: applications };
  },

  /**
   * Get applications by status
   */
  async getApplicationsByStatus(status: ApplicationStatus): Promise<ApiResponse<Application[]>> {
    await delay(500);

    const applications = getApplicationsByStatus(status);
    return { success: true, data: applications };
  },

  /**
   * Get all applications
   */
  async getAllApplications(): Promise<ApiResponse<Application[]>> {
    await delay(700);
    return { success: true, data: mockApplications };
  },

  // ===== Documents =====

  /**
   * Get document by ID
   */
  async getDocument(id: string): Promise<ApiResponse<Document>> {
    await delay(400);

    const document = getDocumentById(id);
    if (!document) {
      return { success: false, error: 'Document not found' };
    }

    return { success: true, data: document };
  },

  /**
   * Get documents by company
   */
  async getCompanyDocuments(companyId: string): Promise<ApiResponse<Document[]>> {
    await delay(600);

    const documents = getDocumentsByCompany(companyId);
    return { success: true, data: documents };
  },

  /**
   * Get expiring documents
   */
  async getExpiringDocuments(daysAhead: number = 30): Promise<ApiResponse<Document[]>> {
    await delay(500);

    const documents = getExpiringDocuments(daysAhead);
    return { success: true, data: documents };
  },

  // ===== Suppliers =====

  /**
   * Get supplier by ID
   */
  async getSupplier(id: string): Promise<ApiResponse<Supplier>> {
    await delay(400);

    const supplier = getSupplierById(id);
    if (!supplier) {
      return { success: false, error: 'Supplier not found' };
    }

    return { success: true, data: supplier };
  },

  /**
   * Get certified suppliers
   */
  async getCertifiedSuppliers(): Promise<ApiResponse<Supplier[]>> {
    await delay(500);

    const suppliers = getCertifiedSuppliers();
    return { success: true, data: suppliers };
  },

  /**
   * Get all suppliers
   */
  async getAllSuppliers(): Promise<ApiResponse<Supplier[]>> {
    await delay(500);
    return { success: true, data: mockSuppliers };
  },

  // ===== Training =====

  /**
   * Get training course by ID
   */
  async getTrainingCourse(id: string): Promise<ApiResponse<TrainingCourse>> {
    await delay(400);

    const course = getCourseById(id);
    if (!course) {
      return { success: false, error: 'Course not found' };
    }

    return { success: true, data: course };
  },

  /**
   * Get all training courses
   */
  async getTrainingCourses(): Promise<ApiResponse<TrainingCourse[]>> {
    await delay(500);
    return { success: true, data: mockTrainingCourses };
  },

  /**
   * Get upcoming training sessions
   */
  async getUpcomingTrainingSessions(): Promise<ApiResponse<TrainingSession[]>> {
    await delay(500);

    const sessions = getUpcomingSessions();
    return { success: true, data: sessions };
  },

  /**
   * Get all training sessions
   */
  async getTrainingSessions(): Promise<ApiResponse<TrainingSession[]>> {
    await delay(500);
    return { success: true, data: mockTrainingSessions };
  },

  /**
   * Get training enrollments
   */
  async getTrainingEnrollments(): Promise<ApiResponse<TrainingEnrollment[]>> {
    await delay(500);
    return { success: true, data: mockTrainingEnrollments };
  },

  // ===== Payments =====

  /**
   * Get payment by ID
   */
  async getPayment(id: string): Promise<ApiResponse<Payment>> {
    await delay(400);

    const payment = getPaymentById(id);
    if (!payment) {
      return { success: false, error: 'Payment not found' };
    }

    return { success: true, data: payment };
  },

  /**
   * Get all payments
   */
  async getPayments(): Promise<ApiResponse<Payment[]>> {
    await delay(600);
    return { success: true, data: mockPayments };
  },

  /**
   * Get payments by status
   */
  async getPaymentsByStatus(status: PaymentStatus): Promise<ApiResponse<Payment[]>> {
    await delay(500);

    const payments = getPaymentsByStatus(status);
    return { success: true, data: payments };
  },

  /**
   * Get all invoices
   */
  async getInvoices(): Promise<ApiResponse<Invoice[]>> {
    await delay(600);
    return { success: true, data: mockInvoices };
  },

  /**
   * Get overdue invoices
   */
  async getOverdueInvoices(): Promise<ApiResponse<Invoice[]>> {
    await delay(500);

    const invoices = getOverdueInvoices();
    return { success: true, data: invoices };
  },

  // ===== Currencies =====

  /**
   * Get currency information
   */
  async getCurrency(code: string): Promise<ApiResponse<CurrencyInfo>> {
    await delay(200);

    const currency = getCurrencyInfo(code as any);
    if (!currency) {
      return { success: false, error: 'Currency not found' };
    }

    return { success: true, data: currency };
  },

  /**
   * Get all currencies
   */
  async getCurrencies(): Promise<ApiResponse<CurrencyInfo[]>> {
    await delay(300);
    return { success: true, data: mockCurrencies };
  },

  /**
   * Get exchange rate
   */
  async getExchangeRate(from: string, to: string): Promise<ApiResponse<ExchangeRate | undefined>> {
    await delay(300);

    const rate = getExchangeRate(from as any, to as any);
    return { success: true, data: rate };
  },

  /**
   * Convert currency
   */
  async convertCurrency(amount: number, from: string, to: string): Promise<ApiResponse<{ convertedAmount: number; rate: number } | null>> {
    await delay(300);

    const result = convertCurrency(amount, from as any, to as any);
    return { success: true, data: result };
  },

  // ===== Checklists =====

  /**
   * Get checklist template by ID
   */
  async getChecklistTemplate(id: string): Promise<ApiResponse<ChecklistTemplate>> {
    await delay(400);

    const template = getChecklistTemplateById(id);
    if (!template) {
      return { success: false, error: 'Checklist template not found' };
    }

    return { success: true, data: template };
  },

  /**
   * Get all checklist templates
   */
  async getChecklistTemplates(): Promise<ApiResponse<ChecklistTemplate[]>> {
    await delay(500);
    return { success: true, data: mockChecklistTemplates };
  },

  /**
   * Get checklist templates by type
   */
  async getChecklistTemplatesByType(type: string): Promise<ApiResponse<ChecklistTemplate[]>> {
    await delay(400);

    const templates = getChecklistTemplatesByType(type as any);
    return { success: true, data: templates };
  },

  // ===== Certificates =====

  /**
   * Get all certificates
   */
  async getCertificates(): Promise<ApiResponse<Certificate[]>> {
    await delay(600);
    return { success: true, data: mockCertificates };
  },

  /**
   * Get certificate by ID
   */
  async getCertificate(id: string): Promise<ApiResponse<Certificate>> {
    await delay(400);

    const certificate = getCertificateById(id);
    if (!certificate) {
      return { success: false, error: 'Certificate not found' };
    }

    return { success: true, data: certificate };
  },

  /**
   * Get certificate by certificate number
   */
  async getCertificateByCertificateNumber(certNumber: string): Promise<ApiResponse<Certificate>> {
    await delay(400);

    const certificate = getCertificateByCertNumber(certNumber);
    if (!certificate) {
      return { success: false, error: 'Certificate not found' };
    }

    return { success: true, data: certificate };
  },

  /**
   * Get certificates by company ID
   */
  async getCertificatesByCompanyId(companyId: string): Promise<ApiResponse<Certificate[]>> {
    await delay(500);

    const certificates = getCertificatesByCompany(companyId);
    return { success: true, data: certificates };
  },

  /**
   * Get certificates by status
   */
  async getCertificatesByStatus(status: CertificateStatus): Promise<ApiResponse<Certificate[]>> {
    await delay(500);

    const certificates = getCertificatesByStatus(status);
    return { success: true, data: certificates };
  },

  /**
   * Get active certificates
   */
  async getActiveCertificates(): Promise<ApiResponse<Certificate[]>> {
    await delay(500);

    const certificates = getActiveCertificates();
    return { success: true, data: certificates };
  },

  /**
   * Get expiring certificates
   */
  async getExpiringCertificates(daysThreshold: number = 90): Promise<ApiResponse<Certificate[]>> {
    await delay(500);

    const certificates = getExpiringCertificates(daysThreshold);
    return { success: true, data: certificates };
  },

  /**
   * Verify certificate
   */
  async verifyCertificate(identifier: string): Promise<ApiResponse<CertificateVerificationResult>> {
    await delay(600);

    const result = verifyCertificate(identifier);
    return { success: true, data: result };
  },

  /**
   * Get certificate statistics
   */
  async getCertificateStatistics(): Promise<ApiResponse<CertificateStatistics>> {
    await delay(500);

    const stats = getCertificateStatistics();
    return { success: true, data: stats };
  },

  // ===== Support & Tickets =====

  /**
   * Get support tickets with optional filters
   */
  async getTicketsApi(filters?: {
    type?: TicketType;
    status?: TicketStatus;
    priority?: TicketPriority;
    assignedTo?: string;
    companyId?: string;
    search?: string;
  }): Promise<ApiResponse<SupportTicket[]>> {
    await delay(400);

    const tickets = getTickets(filters);
    return { success: true, data: tickets };
  },

  /**
   * Get ticket by ID
   */
  async getTicketByIdApi(id: string): Promise<ApiResponse<SupportTicket>> {
    await delay(300);

    const ticket = getTicketById(id);
    if (!ticket) {
      return { success: false, error: 'Ticket not found' };
    }

    return { success: true, data: ticket };
  },

  /**
   * Create new support ticket
   */
  async createTicket(data: {
    type: TicketType;
    priority: TicketPriority;
    subject: string;
    description: string;
    companyId: string;
    companyName: string;
    createdBy: string;
    createdByName: string;
    relatedApplicationId?: string;
  }): Promise<ApiResponse<SupportTicket>> {
    await delay(600);

    const newTicket: SupportTicket = {
      id: `ticket-${Date.now()}`,
      ticketNumber: `TKT-2026-${String(mockTickets.length + 1).padStart(5, '0')}`,
      ...data,
      status: 'open' as TicketStatus,
      createdAt: new Date(),
      updatedAt: new Date(),
      tags: [],
      attachments: [],
      messageCount: 0,
    };

    mockTickets.push(newTicket);
    return { success: true, data: newTicket, message: 'Ticket created successfully' };
  },

  /**
   * Update ticket
   */
  async updateTicket(id: string, updates: Partial<SupportTicket>): Promise<ApiResponse<SupportTicket>> {
    await delay(400);

    const ticketIndex = mockTickets.findIndex((t) => t.id === id);
    if (ticketIndex === -1) {
      return { success: false, error: 'Ticket not found' };
    }

    mockTickets[ticketIndex] = {
      ...mockTickets[ticketIndex],
      ...updates,
      updatedAt: new Date(),
    };

    return { success: true, data: mockTickets[ticketIndex], message: 'Ticket updated successfully' };
  },

  /**
   * Assign ticket to support staff
   */
  async assignTicket(id: string, assigneeId: string, assigneeName: string, assigneeRole: 'consultant' | 'support_staff' | 'trainer' | 'expert'): Promise<ApiResponse<SupportTicket>> {
    await delay(400);

    return this.updateTicket(id, {
      assignedTo: assigneeId,
      assignedToName: assigneeName,
      assignedToRole: assigneeRole,
      assignedAt: new Date(),
    });
  },

  /**
   * Close ticket with optional rating
   */
  async closeTicket(id: string, rating?: number, ratingFeedback?: string): Promise<ApiResponse<SupportTicket>> {
    await delay(500);

    return this.updateTicket(id, {
      status: 'closed' as TicketStatus,
      closedAt: new Date(),
      rating,
      ratingFeedback,
    });
  },

  /**
   * Get ticket messages
   */
  async getTicketMessagesApi(ticketId: string): Promise<ApiResponse<TicketMessage[]>> {
    await delay(300);

    const messages = getTicketMessages(ticketId);
    return { success: true, data: messages };
  },

  /**
   * Add message to ticket
   */
  async addTicketMessage(ticketId: string, message: string, authorId: string, authorName: string, authorRole: 'company' | 'consultant' | 'auditor' | 'admin' | 'support_staff', isInternal: boolean = false): Promise<ApiResponse<TicketMessage>> {
    await delay(500);

    const newMessage: TicketMessage = {
      id: `msg-${Date.now()}`,
      ticketId,
      authorId,
      authorName,
      authorRole,
      message,
      isInternal,
      attachments: [],
      createdAt: new Date(),
    };

    mockTicketMessages.push(newMessage);

    // Update ticket message count
    const ticketIndex = mockTickets.findIndex((t) => t.id === ticketId);
    if (ticketIndex !== -1) {
      mockTickets[ticketIndex].messageCount += 1;
      mockTickets[ticketIndex].updatedAt = new Date();
    }

    return { success: true, data: newMessage, message: 'Message added successfully' };
  },

  /**
   * Get knowledge base articles
   */
  async getKnowledgeBaseArticlesApi(category?: KnowledgeBaseCategory): Promise<ApiResponse<KnowledgeBaseArticle[]>> {
    await delay(400);

    const articles = getKnowledgeBaseArticles(category);
    return { success: true, data: articles };
  },

  /**
   * Get article by slug
   */
  async getArticleBySlugApi(slug: string): Promise<ApiResponse<KnowledgeBaseArticle>> {
    await delay(300);

    const article = getArticleBySlug(slug);
    if (!article) {
      return { success: false, error: 'Article not found' };
    }

    // Increment view count
    article.viewCount += 1;

    return { success: true, data: article };
  },

  /**
   * Search articles
   */
  async searchArticles(query: string): Promise<ApiResponse<KnowledgeBaseArticle[]>> {
    await delay(400);

    const lowerQuery = query.toLowerCase();
    const results = mockKnowledgeBaseArticles.filter(
      (article) =>
        article.title.toLowerCase().includes(lowerQuery) ||
        article.excerpt.toLowerCase().includes(lowerQuery) ||
        article.content.toLowerCase().includes(lowerQuery) ||
        article.tags.some((tag) => tag.toLowerCase().includes(lowerQuery))
    );

    return { success: true, data: results };
  },

  /**
   * Mark article as helpful/not helpful
   */
  async markArticleHelpful(id: string, helpful: boolean): Promise<ApiResponse<void>> {
    await delay(300);

    const article = mockKnowledgeBaseArticles.find((a) => a.id === id);
    if (!article) {
      return { success: false, error: 'Article not found' };
    }

    if (helpful) {
      article.helpful += 1;
    } else {
      article.notHelpful += 1;
    }

    return { success: true, message: 'Feedback recorded' };
  },

  /**
   * Get FAQs
   */
  async getFAQsApi(category?: KnowledgeBaseCategory): Promise<ApiResponse<FAQ[]>> {
    await delay(300);

    const faqs = getFAQs(category);
    return { success: true, data: faqs };
  },

  /**
   * Mark FAQ as helpful/not helpful
   */
  async markFAQHelpful(id: string, helpful: boolean): Promise<ApiResponse<void>> {
    await delay(300);

    const faq = mockFAQs.find((f) => f.id === id);
    if (!faq) {
      return { success: false, error: 'FAQ not found' };
    }

    if (helpful) {
      faq.helpful += 1;
    } else {
      faq.notHelpful += 1;
    }

    return { success: true, message: 'Feedback recorded' };
  },

  /**
   * Get support statistics
   */
  async getSupportStatsApi(): Promise<ApiResponse<SupportStats>> {
    await delay(400);

    const stats = getSupportStats();
    return { success: true, data: stats };
  },
};

export default mockApi;
